import { getCliniqueWebsiteForImageUrl } from "../../constantes/utilitaries";
import { fetchLokiVariants, normalizeLokiProductUrl } from "./lokiProducts";

type SppProductRow = {
    sku: string,
    name: string,
    url: string,
    status: string,
}

type CmsProduct = {
    BASE_ID?: string,
    NAME?: string,
    PRODUCT_NAME?: string,
    PROD_BASE_ID?: string,
    PRODUCT_CODE?: string,
}

type ElcProduct = {
    product_id?: string,
    product_url?: string,
    display_name?: string,
}

const BATCHER_MARKET = "US";
const BATCHER_FILE_NAME = "clinique-us-spp-urls.csv";
const BATCHER_SKU_CHUNK_SIZE = 7;
const CMS_PRODUCT_URL = "https://cms-prod.cliniquecomputer.com/api/micro_sites/products";
const CMS_PRODUCT_AUTH = "Basic bWljcm9zaXRlOjJrV2RzM09yV3NTU1NEcG1KV2U3QVlhMWk4T3BwQ3pQbDg3NG8xWWdheTVZSVRGb0RkRUsybFBXdUZHSw==";

const updateBatcherStatus = (message: string) => {
    console.info(`[SPP batcher] ${message}`);
    const root = document.getElementById("root");

    if (root) {
        root.innerHTML = `<div style="font-family: Arial, sans-serif; padding: 24px;">${message}</div>`;
    }
}

const chunk = <T,>(items: T[], size: number): T[][] => {
    const chunks: T[][] = [];

    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }

    return chunks;
}

const getAbsoluteSppUrl = (sppURL: string): string => {
    if (!sppURL) {
        return "";
    }

    try {
        return new URL(sppURL, getCliniqueWebsiteForImageUrl(BATCHER_MARKET)).toString();
    } catch {
        return "";
    }
}

const fetchCmsProductMap = async (): Promise<Map<string, CmsProduct>> => {
    const response = await fetch(CMS_PRODUCT_URL, {
        method: "POST",
        headers: {
            Authorization: CMS_PRODUCT_AUTH,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            countryCode: "us",
            languageCode: "en_us",
            currencyId: 1,
        }),
        redirect: "follow",
    });
    const json = await response.json();
    const productsBySku = new Map<string, CmsProduct>();

    Object.keys(json || {}).forEach((productKey) => {
        const productItems = json[productKey];

        if (!Array.isArray(productItems)) {
            return;
        }

        productItems.forEach((product: CmsProduct) => {
            if (product.BASE_ID && !productsBySku.has(product.BASE_ID)) {
                productsBySku.set(product.BASE_ID, product);
            }
        });
    });

    return productsBySku;
}

const fetchProductsBySkus = async (variantIds: string[]): Promise<Map<string, ElcProduct>> => {
    const variants = await fetchLokiVariants({ variantIds });
    const productsByProductId = new Map<string, ElcProduct>();
    variants.forEach((variant) => {
        const product = variant.product;
        if (product?.id != null) {
            const productId = String(product.id);
            productsByProductId.set(productId, {
                product_id: productId,
                product_url: normalizeLokiProductUrl(product.canonical_path),
                display_name: product.full_name || product.name || "",
            });
        }
    });

    return productsByProductId;
}

const csvEscape = (value: string | number | undefined): string => {
    const stringValue = String(value ?? "");

    if (/[",\n\r]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
}

const makeCsv = (rows: SppProductRow[]): string => {
    return [
        ["sku", "nom produit", "URL", "statut"].join(","),
        ...rows
            .sort((a, b) => a.sku.localeCompare(b.sku))
            .map((row) => [
                csvEscape(row.sku),
                csvEscape(row.name),
                csvEscape(row.url),
                csvEscape(row.status),
            ].join(",")),
    ].join("\n");
}

const downloadCsv = (csv: string) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = BATCHER_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export const runSppBatcher = async () => {
    try {
        updateBatcherStatus("Recuperation des produits CMS US");
        const cmsProductsBySku = await fetchCmsProductMap();
        const cmsProducts = Array.from(cmsProductsBySku.values());
        const skus = Array.from(cmsProductsBySku.keys());
        const rowsBySku = new Map<string, SppProductRow>();
        const cmsProductChunks = chunk(cmsProducts, BATCHER_SKU_CHUNK_SIZE);
        const elcProductsByProductId = new Map<string, ElcProduct>();

        if (cmsProducts.length === 0) {
            updateBatcherStatus("Aucun produit retourne par le CMS US.");
            return;
        }

        for (let index = 0; index < cmsProductChunks.length; index++) {
            const variantIds = Array.from(new Set(
                cmsProductChunks[index]
                    .map((product) => product.PRODUCT_CODE)
                    .filter((sku): sku is string => Boolean(sku))
            ));

            updateBatcherStatus(`Recuperation des URLs SPP US ${index + 1}/${cmsProductChunks.length}`);

            if (variantIds.length === 0) {
                continue;
            }

            const products = await fetchProductsBySkus(variantIds);

            products.forEach((product, productId) => {
                elcProductsByProductId.set(productId, product);
            });
        }

        skus.forEach((sku) => {
            const cmsProduct = cmsProductsBySku.get(sku);
            const productId = cmsProduct?.PROD_BASE_ID;
            const elcProduct = productId ? elcProductsByProductId.get(productId) : undefined;
            const url = getAbsoluteSppUrl(elcProduct?.product_url || "");
            let status = "OK";

            if (!cmsProduct) {
                status = "SKU non trouve dans le CMS US";
            } else if (!productId) {
                status = "SKU trouve dans le CMS US mais sans PROD_BASE_ID";
            } else if (!elcProduct) {
                status = "PROD_BASE_ID absent de l'API produit ELC";
            } else if (!url) {
                status = "Produit trouve dans l'API produit ELC mais sans URL SPP";
            }

            rowsBySku.set(sku, {
                sku,
                name: elcProduct?.display_name || cmsProduct?.PRODUCT_NAME || cmsProduct?.NAME || "",
                url,
                status,
            });
        });

        const foundRows = Array.from(rowsBySku.values());
        const missingSkus = skus.filter((sku) => !rowsBySku.has(sku) || !rowsBySku.get(sku)?.url);
        const csv = makeCsv(foundRows);

        downloadCsv(csv);
        updateBatcherStatus(`CSV genere: ${foundRows.length} lignes. SKU sans URL: ${missingSkus.length}`);

        if (missingSkus.length > 0) {
            console.warn("[SPP batcher] missing SKUs", missingSkus);
        }
    } catch (error) {
        console.error("[SPP batcher] error", error);
        updateBatcherStatus("Erreur pendant la generation du CSV SPP. Voir la console pour le detail.");
    }
}
